import {
  ref, Ref, watch, computed, ComputedRef, useContext,
} from '@nuxtjs/composition-api';
import { Product } from '~/modules/catalog/product/types';
import {
  getAttributeLabel,
  getAvailableType,
  getCompositionOfFertilizer,
  getCrossSellProducts,
  getDescription,
  getIsNewProduct,
  getName,
  getPrice,
  getProductAttachments,
  getQtyConfig,
  getRelatedProducts,
  getShortDescription,
  getUpsellProducts,
  isProductBestseller,
  isSpecialPrice,
  getAdditionalFields,
  getIsOutOfStatus,
  getProductThumbnailImage, getAttrsIDList, getEan,
  getPakowanieHurtoweProduktu, isProductRecommended,
} from '~/modules/catalog/product/getters/productGetters';
import useOmnibusPrice from '~/modules/catalog/product/composables/useOmnibusPrice';
import { Aggregation } from '~/modules/GraphQL/types';
import { useImage } from '~/composables';
import useProductGallery from '~/modules/catalog/product/composables/useProductGallery';
import { getValueFromTable } from '~/modules/catalog/product/helpers/stockHelpers';
import getFormattedDate from '~/helpers/getFormattedDate';

export interface LinkLabel {
  label: string;
  link: string;
}

export function useProductHandler(product: Ref<Product>, attributeValues: Ref<Aggregation[]>) {
  const productRef = ref<Product>(product.value);
  const attributeValuesRef = ref<Aggregation[]>(attributeValues.value);

  const { getProductOmnibusPriceBySKU } = useOmnibusPrice();
  const { getMagentoImage } = useImage();
  watch(product, (newProduct) => {
    productRef.value = newProduct;
  });
  const { i18n } = useContext();

  const currentProduct = computed(
    () => productRef.value?.configurable_product_options_selection?.variant ?? productRef.value,
  ) as ComputedRef<Product>;

  const isVariantSelected = computed(() => !!productRef.value?.configurable_product_options_selection?.variant);

  const { productGallery, imageSizes } = useProductGallery(productRef);

  const title = computed(() => getName(currentProduct.value));
  const qtyConfig = computed(() => getQtyConfig(currentProduct.value));
  const productDescription = computed(() => getDescription(currentProduct.value));
  const productShortDescription = computed(() => getShortDescription(currentProduct.value));
  const pakowanieHurtoweProduktu = computed(() => getPakowanieHurtoweProduktu(currentProduct.value));
  const omnibusPrice = computed(() => getProductOmnibusPriceBySKU(currentProduct.value.sku));
  // const regularPrice = computed(() => getPrice(currentProduct.value).regular);
  // const specialPrice = computed(() => (isSpecialPrice(getPrice(currentProduct.value).special, omnibusPrice.value)
  //   ? getPrice(currentProduct.value).special : ''));

  const finalPrice = computed(() => getPrice(currentProduct.value).final);
  const finalNettoPrice = computed(() => getPrice(currentProduct.value).finalNetto);

  const currentOmnibusPrice = computed(() => (isSpecialPrice(getPrice(currentProduct.value).special, omnibusPrice.value) ? omnibusPrice.value : ''));

  const manufacturerLabel = computed(() => (productRef.value.manufacturer ? getAttributeLabel('manufacturer', productRef.value.manufacturer, attributeValuesRef.value) : ''));
  const ean = computed(() => getEan(currentProduct.value));
  const available = computed(() => getAvailableType(currentProduct.value));
  const productAttachments = computed(() => getProductAttachments(productRef.value));
  const isNew = computed(() => getIsNewProduct(currentProduct.value));
  const isBestseller = computed(() => isProductBestseller(currentProduct.value));
  const isRecommended = computed(() => isProductRecommended(currentProduct.value));
  const compositionOfFertilizer = computed(() => getCompositionOfFertilizer(currentProduct.value, attributeValuesRef.value));
  const crosssellProductsSKUs = computed(() => getCrossSellProducts(productRef.value));
  const upsellProductsSKUs = computed(() => getUpsellProducts(productRef.value));
  const relatedProductsSKUs = computed(() => getRelatedProducts(productRef.value));
  const productAdditionalFields = computed(() => getAdditionalFields(currentProduct.value, attributeValuesRef.value));

  const isAvailableInStock = computed(() => available.value.stock_status === 'IN_STOCK');
  const isOutOfStatus = computed(() => getIsOutOfStatus(productRef.value));
  // @ts-ignore
  const bundleProductItems = computed(() => currentProduct.value?.items);

  const thumbnailImage = computed(() => getMagentoImage(getProductThumbnailImage(currentProduct.value)));
  const sortedProductGallery = computed(() => productGallery.value.sort((a, b) => {
    if (a.big.url === thumbnailImage.value) return -1;
    if (b.big.url === thumbnailImage.value) return 1;
    return 0;
  }));

  const deliveryTimeLabel: Ref<string> = computed(
    () => getValueFromTable(
      currentProduct.value.delivery_time,
      i18n,
    ),
  );

  function createComputedAttribute(attributeKey: string, productScope: 'parent' | 'child' = 'parent'): ComputedRef<LinkLabel[]> {
    const useProduct = productScope === 'parent' ? productRef : currentProduct;
    return computed(() => {
      const ids = getAttrsIDList(useProduct.value, attributeKey);
      return ids.length > 0
        ? ids.map((i) => ({
          label: getAttributeLabel(attributeKey, i, attributeValuesRef.value),
          link: `/sklep?${attributeKey}=${i}`,
        }))
        : [];
    });
  }

  const purpose = createComputedAttribute('purpose');
  const czynnikiStresowe = createComputedAttribute('czynniki_stresowe');
  const kondycjaRosliny = createComputedAttribute('kondycja_rosliny');
  const substancjaCzynna = createComputedAttribute('substancja_czynna');
  const korzysciStosowania = createComputedAttribute('korzysci_stosowania');
  const regeneracjaUpraw = createComputedAttribute('Regeneracja_upraw');
  const aktywacjaUpraw = createComputedAttribute('aktywacja_upraw');
  const budowaniePlonuIJakosci = createComputedAttribute('Budowanie_plonu_i_jakosci');
  const zwiekszanieOdpornosciUpraw = createComputedAttribute('zwiekszanie_odpornosci_upraw');
  const sklad = computed(() => productRef.value.sklad || '');
  const terminWaznosci = computed(() => (currentProduct.value.termin_waznosci_produktu ? getFormattedDate(currentProduct.value.termin_waznosci_produktu) : ''));
  const formaPakowania = createComputedAttribute('forma_pakowania', 'child');
  const formaTransportu = createComputedAttribute('forma_transportu', 'child');

  return {
    currentProduct,
    title,
    qtyConfig,
    isVariantSelected,
    productDescription,
    productShortDescription,
    finalPrice,
    finalNettoPrice,
    currentOmnibusPrice,
    manufacturerLabel,
    ean,
    available,
    productAttachments,
    isNew,
    isBestseller,
    crosssellProductsSKUs,
    upsellProductsSKUs,
    relatedProductsSKUs,
    isAvailableInStock,
    bundleProductItems,
    compositionOfFertilizer,
    productAdditionalFields,
    isOutOfStatus,
    productGallery,
    imageSizes,
    sortedProductGallery,
    deliveryTimeLabel,
    purpose,
    czynnikiStresowe,
    korzysciStosowania,
    substancjaCzynna,
    kondycjaRosliny,
    regeneracjaUpraw,
    aktywacjaUpraw,
    budowaniePlonuIJakosci,
    zwiekszanieOdpornosciUpraw,
    sklad,
    terminWaznosci,
    formaPakowania,
    formaTransportu,
    pakowanieHurtoweProduktu,
    isRecommended,
  };
}
